import {
  castArray,
  find,
  findKey,
  flow,
  get,
  getOr,
  head,
  identity,
  isArray,
  isEmpty,
  isUndefined,
  map,
  replace,
  pick,
  split,
  startsWith,
  toUpper,
  toString,
  defaults,
} from 'lodash/fp';

import getMapper, { cleanEmptyValues, mapPath } from 'helpers/mapper';
import { options as adSellerTypeOptions } from 'helpers/fieldOptions/ad/sellerType';
import { values as adSortValues } from 'helpers/fieldOptions/ad/sort';
import { values as airbagValues } from 'helpers/fieldOptions/car/airbag';
import { values as additionalValues } from 'helpers/fieldOptions/car/additional';
import { values as multimediaValues } from 'helpers/fieldOptions/car/multimedia';
import { values as comfortValues } from 'helpers/fieldOptions/car/comfort';
import { values as seatComfortValues } from 'helpers/fieldOptions/car/seatComfort';
import { values as lightValues } from 'helpers/fieldOptions/car/light';
import { values as carColorValues } from 'helpers/fieldOptions/car/color';
import { values as carExtraValues } from 'helpers/fieldOptions/car/extra';
import { values as carFuelTypeValues } from 'helpers/fieldOptions/car/fuelType';
import { values as carInteriorValues } from 'helpers/fieldOptions/car/interior';
import { values as carParkingSensorValues } from 'helpers/fieldOptions/car/parkingSensor';
import { values as carSecurityValues } from 'helpers/fieldOptions/car/security';
import { values as carServiceHistoryValues } from 'helpers/fieldOptions/car/serviceHistory';
import { values as carTransmissionValues } from 'helpers/fieldOptions/car/transmission';
import { values as priceTypeValues } from 'helpers/fieldOptions/ad/priceType';
import { values as upholsteryValues } from 'helpers/fieldOptions/car/upholstery';
import { encodeName } from 'helpers/fieldOptions';
import { inputBoxesFromArray } from 'components/forms/InputBoxes';

import {
  ADDITIONAL_NAME,
  AIRBAG_NAME,
  BODY_TYPE_NAME,
  COLOR_NAME,
  COMFORT_NAME,
  CONSUMPTION_RANGE_NAME,
  DEFAULT_GEOLOCATION_DISTANCE,
  DISTANCE_NAME,
  DOOR_RANGE_NAME,
  EMISSION_STICKER_NAME,
  ENV_STANDARD_NAME,
  EXTRAS_NAME,
  FUEL_TYPE_NAME,
  GEOLOCATION_NAME,
  HAS_IMAGES_NAME,
  INTERIOR_NAME,
  LIGHT_NAME,
  LOCALE_NAME,
  MAKE_NAME,
  METALLIC_NAME,
  MILEAGE_RANGE_NAME,
  MODEL_NAME,
  MONTHLY_PRICE_RANGE_NAME,
  MULTIMEDIA_NAME,
  NOT_DAMAGED_NAME,
  PAGE_NAME,
  PAGE_SIZE_NAME,
  PARKING_SENSORS_NAME,
  POWER_RANGE_NAME,
  PREV_OWNER_NAME,
  PRICE_RANGE_NAME,
  PRICE_TYPE_NAME,
  SEAT_COMFORT_NAME,
  SEAT_RANGE_NAME,
  SECURITY_NAME,
  SELLER_ID_NAME,
  SELLER_TYPE_NAME,
  SERVICE_HISTORY_NAME,
  SMOKER_NAME,
  SORT_NAME,
  SUB_TYPE_NAME,
  TRANSMISSION_NAME,
  UPHOLSTERY_NAME,
  VEHICLE_NAME,
  YEAR_RANGE_NAME,
} from '../../constants';

import scaffolding from '../formScaffolding';

const toArray = (item) => (isArray(item) || isUndefined(item) ? item : [item]);

const getCheckboxList = (path, getter = identity) =>
  flow(get(path), toArray, map(getter), inputBoxesFromArray(encodeName));

const getByKey = (path, getter = identity) => flow(get(path), getter);
const mapByKey = (mapping) => (key) => get([key], mapping);

const movePath = (path, getter = get, ...args) =>
  mapPath(path, getter(path, ...args));

const getMake = (carMakeValues) =>
  flow(get(['brand']), castArray, head, (make) => get([make], carMakeValues));

const getModel = (carMakeValues) => (query) => {
  const make = getMake(carMakeValues)(query);
  const makeKey = findKey((value) => value === make, carMakeValues);

  return flow(
    get(['model']),
    castArray,
    find(startsWith(makeKey)),
    split('.'),
    get([1]),
    replace('_', '.'),
    toUpper,
  )(query);
};

const getSubType = (carMakeValues) => (query) => {
  const make = getMake(carMakeValues)(query);
  const makeKey = findKey((value) => value === make, carMakeValues);

  return flow(
    get(['subType']),
    castArray,
    find(startsWith(makeKey)),
    split('.'),
    get([2]),
    replace('_', '.'),
    toUpper,
  )(query);
};

const getRange =
  (format = identity) =>
  (path) =>
  (item) => {
    const key = flow(get([0]), replace('Range', ''))(path);
    const minKey = `${key}Min`;
    const maxKey = `${key}Max`;

    let range = {};

    const minValue = item[minKey];
    const maxValue = item[maxKey];

    if (minValue) {
      range = { ...range, min: format(minValue) };
    }

    if (maxValue) {
      range = { ...range, max: format(maxValue) };
    }

    if (!isEmpty(range)) {
      return range;
    }
  };

const getPage = (path) => getOr('1', path);

const getPriceType = (query) => {
  const priceGetter = getRange()([PRICE_RANGE_NAME]);
  const monthlyPriceGetter = getRange()([MONTHLY_PRICE_RANGE_NAME]);

  const price = priceGetter(query);
  const monthlyPrice = monthlyPriceGetter(query);
  const priceType = get([PRICE_TYPE_NAME])(query);

  if (priceType) {
    return priceType;
  }

  if (price && monthlyPrice) {
    return priceTypeValues.BUY_PRICE;
  }

  if (monthlyPrice) {
    return priceTypeValues.MONTHLY_PRICE;
  }

  return priceTypeValues.BUY_PRICE;
};

const getSellerType = () =>
  flow(
    get(['provider']),
    toString,
    (provider) => find({ provider })(adSellerTypeOptions),
    get(['value']),
  );

const getGeoLocation = () =>
  flow(pick(['city', 'distance', 'lat', 'lng']), (geoLocation) => {
    const { lat, lng } = geoLocation;

    if (lat && lng) {
      return defaults(
        { [DISTANCE_NAME]: toString(DEFAULT_GEOLOCATION_DISTANCE) },
        geoLocation,
      );
    }

    return geoLocation;
  });

const getVehicle = (carMakeValues) => (query) => {
  const make = getMake(carMakeValues)(query);
  const model = getModel(carMakeValues)(query);
  const subType = getSubType(carMakeValues)(query);

  let vehicle;

  if (make && model && subType) {
    vehicle = {
      [MAKE_NAME]: make,
      [MODEL_NAME]: model,
      [SUB_TYPE_NAME]: subType,
    };
  } else if (make && model) {
    vehicle = {
      [MAKE_NAME]: make,
      [MODEL_NAME]: model,
    };
  } else if (make) {
    vehicle = {
      [MAKE_NAME]: make,
    };
  }

  if (isEmpty(vehicle)) {
    return;
  }

  return [vehicle];
};

const castToNumber = (path) => flow(get(path), Number);

const fromReadableBool = (value) => {
  if (value === 'no') {
    return false;
  } else if (value === 'yes') {
    return true;
  }

  return undefined;
};

export default (carMakeValues = {}) =>
  (source) =>
    getMapper(
      [
        movePath([AIRBAG_NAME], getByKey, mapByKey(airbagValues)),
        movePath(
          [ADDITIONAL_NAME],
          getCheckboxList,
          mapByKey(additionalValues),
        ),
        movePath([BODY_TYPE_NAME], getCheckboxList),
        movePath([COLOR_NAME], getCheckboxList, mapByKey(carColorValues)),
        movePath([COMFORT_NAME], getCheckboxList, mapByKey(comfortValues)),
        movePath([CONSUMPTION_RANGE_NAME], getRange()),
        movePath([DOOR_RANGE_NAME], getRange()),
        movePath([EMISSION_STICKER_NAME]),
        mapPath([ENV_STANDARD_NAME], castToNumber([ENV_STANDARD_NAME])),
        movePath([EXTRAS_NAME], getCheckboxList, mapByKey(carExtraValues)),
        movePath(
          [FUEL_TYPE_NAME],
          getCheckboxList,
          mapByKey(carFuelTypeValues),
        ),
        movePath([GEOLOCATION_NAME], getGeoLocation),
        movePath([INTERIOR_NAME], getCheckboxList, mapByKey(carInteriorValues)),
        movePath([LIGHT_NAME], getCheckboxList, mapByKey(lightValues)),
        movePath([LOCALE_NAME]),
        movePath([METALLIC_NAME], getCheckboxList),
        movePath([MILEAGE_RANGE_NAME], getRange()),
        movePath(
          [MULTIMEDIA_NAME],
          getCheckboxList,
          mapByKey(multimediaValues),
        ),
        movePath([PAGE_NAME], getPage),
        movePath([PAGE_SIZE_NAME]),
        movePath(
          [PARKING_SENSORS_NAME],
          getCheckboxList,
          mapByKey(carParkingSensorValues),
        ),
        movePath([POWER_RANGE_NAME], getRange()),
        movePath([PREV_OWNER_NAME], getRange()),
        movePath([PRICE_RANGE_NAME], getRange()),
        mapPath([PRICE_TYPE_NAME], getPriceType),
        movePath([MONTHLY_PRICE_RANGE_NAME], getRange()),
        movePath(
          [SEAT_COMFORT_NAME],
          getCheckboxList,
          mapByKey(seatComfortValues),
        ),
        movePath([SEAT_RANGE_NAME], getRange()),
        movePath([SECURITY_NAME], getCheckboxList, mapByKey(carSecurityValues)),
        movePath([SELLER_ID_NAME]),
        movePath([SELLER_TYPE_NAME], getSellerType),
        movePath(
          [SERVICE_HISTORY_NAME],
          getCheckboxList,
          mapByKey(carServiceHistoryValues),
        ),
        movePath([SORT_NAME], getByKey, mapByKey(adSortValues)),
        movePath(
          [TRANSMISSION_NAME],
          getCheckboxList,
          mapByKey(carTransmissionValues),
        ),
        mapPath([VEHICLE_NAME], getVehicle(carMakeValues)),
        movePath(
          [UPHOLSTERY_NAME],
          getCheckboxList,
          mapByKey(upholsteryValues),
        ),
        movePath([YEAR_RANGE_NAME], getRange(Number)),
        mapPath(
          [NOT_DAMAGED_NAME],
          flow(get(['notDamaged']), fromReadableBool),
        ),
        mapPath([SMOKER_NAME], flow(get(['smoker']), fromReadableBool)),
        mapPath([HAS_IMAGES_NAME], flow(get(['hasImages']), fromReadableBool)),
        cleanEmptyValues,
      ],
      scaffolding,
    )()(source);
